'use client'

import React from 'react'
import { Avatar, AvatarImage, AvatarFallback } from '../../../Avatar'
import { cn, generateInitials } from '../../../../../lib/utils'

export type ContactTileProps = {
  timestamp?: string
  unReadMessages: number
  isSelected: boolean
  lastMessage: string
  id: string
  href?: string
  img?: string
  name: string
  handleClick?: () => void
}

export const ContactTile = (props: ContactTileProps) => {
  const {
    id,
    img,
    name,
    href,
    lastMessage,
    isSelected,
    unReadMessages,
    handleClick,
    timestamp,
  } = props

  return (
    <button
      className={cn(
        'h-12 rounded-lg flex items-center px-2 py-2 flex flex-row gap-3 w-full transition-colors duration-200 group',
        'hover:bg-muted/70 focus:bg-muted/70 focus:outline-none',
        {
          'bg-accent hover:bg-accent/90 text-accent-foreground': isSelected,
        },
      )}
      title={`contact-${id}`}
      onClick={() => handleClick && handleClick()}
    >
      {href ? <a href={href} /> : null}
      <Avatar
        className={cn(
          'flex h-8 w-8 rounded-full text-xs overflow-hidden shadow-sm border',
          {
            'bg-accent ring-2 ring-accent/20': isSelected,
            'border-2 border-foreground': unReadMessages > 0 && !isSelected,
          },
        )}
      >
        <AvatarImage src={img} seed={id} />
        <AvatarFallback>{generateInitials(name)}</AvatarFallback>
      </Avatar>
      <div
        className={cn(
          'flex flex-col justify-center text-start overflow-hidden w-full gap-0.5',
          {
            'text-primary': !isSelected,
          },
        )}
      >
        <div className="flex flex-row items-center gap-2 overflow-hidden">
          <div
            className={cn(
              'w-fit text-xs truncate border-b border-dotted transition-colors duration-200',
              isSelected
                ? 'font-semibold text-accent-foreground border-accent-foreground/40'
                : unReadMessages > 0
                  ? 'font-semibold text-primary border-primary/40'
                  : 'font-normal text-primary/60 group-hover:text-primary/80 border-primary/40',
            )}
          >
            {name}
          </div>
          {timestamp && (
            <div className="text-[10px] text-muted-foreground/60 shrink-0">
              {timestamp}
            </div>
          )}
        </div>
        <div
          className={cn(
            'text-[10px] truncate',
            isSelected
              ? 'text-accent-foreground/90'
              : unReadMessages > 0
                ? 'text-foreground/90 font-medium'
                : 'text-muted-foreground/50 group-hover:text-muted-foreground/70',
          )}
        >
          {lastMessage}
        </div>
      </div>
      {unReadMessages > 0 && (
        <div className="shrink-0">
          <div
            className={cn(
              'pointer-events-none flex h-5 min-w-5 items-center justify-center rounded-md px-1 text-xs font-medium tabular-nums select-none',
              isSelected ? 'text-accent-foreground' : 'text-sidebar-foreground',
            )}
          >
            {unReadMessages > 99 ? '99+' : unReadMessages}
          </div>
        </div>
      )}
    </button>
  )
}
